import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { GetUserAddressesResponse, UserAddressItem } from '@dreamai/types';
import SecureModal from './SecureModal';
import { ReactComponent as NoLongerNeededIcon } from '../../../icons/no-longer-needed.svg';
import {
  detectCardNetwork,
  getLastFour,
  luhnValid,
  parseExpiry,
  expiryIsFuture,
} from '../../../lib/cardNetworkDetect';
import api from '../../../api';
import './add-payment-card-modal.scss';

export interface AddPaymentCardResult {
  credentialId: string;
  label: string;
  cardLastFour: string;
  cardNetwork: string;
}

interface AddPaymentCardModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: (card: AddPaymentCardResult) => void;
  /**
   * When set, signals that this form is REPLACING an existing saved card
   * (e.g. an expired or declined card). The modal shows an update banner and
   * adjusts copy. The card itself is still saved as a NEW user_credentials row
   * — the old one is just marked inactive by the BE if needed elsewhere.
   */
  existingCredentialId?: string;
  /**
   * Optional human-readable label of the card being replaced. Shown in the
   * update banner so the user knows which card they're replacing.
   */
  existingCardLabel?: string;
  /**
   * Prefilled values for the form. Only used when `existingCredentialId` is
   * set — i.e. the user hit Edit on an existing card in the Memories UI and
   * we've already revealed the vaulted fields. Expected keys mirror the
   * vault shape written by `storeAndVaultCredential`:
   *   cardholderName, cardNumber, expiryDate, cvv,
   *   billingAddressLine1, billingAddressLine2, billingCity,
   *   billingPostcode, billingCountry
   * plus the (metadata-only) `nickname` for the label.
   */
  prefillFields?: Record<string, string>;
  /**
   * Optional "Remove task" callback. When provided, a left-aligned button
   * appears in the footer (matches SecureCredentialModal). Used when this
   * modal is rendered for a response artifact and the user wants to dismiss
   * the task instead of providing a card.
   */
  onAcknowledge?: () => void;
  /** Disable the submit button (e.g. while a parent is processing). */
  readOnly?: boolean;
}

const AddPaymentCardModal: React.FC<AddPaymentCardModalProps> = ({
  show,
  onClose,
  onSuccess,
  existingCredentialId,
  existingCardLabel,
  prefillFields,
  onAcknowledge,
  readOnly,
}) => {
  const isUpdate = !!existingCredentialId;
  const { getAccessTokenSilently } = useAuth0();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nickname, setNickname] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('');

  const [showCvv, setShowCvv] = useState(false);
  const [knownAddresses, setKnownAddresses] = useState<UserAddressItem[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  // Field-level errors keyed by FE field name. Populated either by the
  // pre-flight check inside handleSubmit, or by 400 responses from
  // `/memories/store-credential` that carry `{ error, field, code }`.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const detectedNetwork = useMemo(
    () => detectCardNetwork(cardNumber),
    [cardNumber],
  );

  // Apply prefill fields whenever they change and the modal is open.
  // Used for the Edit-card flow in the Memories UI: the caller reveals
  // the vault contents and passes them here so the user sees what was
  // previously stored (including CVV) and can tweak individual fields.
  //
  // The incoming `cardNumber` is digits-only (how we store it in the
  // vault); we format it with spaces for readability — the same pattern
  // the detectCardNetwork / onChange handlers tolerate.
  useEffect(() => {
    if (!show || !prefillFields) return;
    const formatPan = (digits: string) => {
      const d = (digits || '').replace(/\D/g, '');
      // 4-digit groups. Amex is 4-6-5 officially but space-every-4 reads
      // fine and the BE doesn't care about the visual grouping.
      return d.replace(/(\d{4})(?=\d)/g, '$1 ');
    };
    if (prefillFields.nickname !== undefined)
      setNickname(prefillFields.nickname);
    else if (existingCardLabel) setNickname(existingCardLabel);
    if (prefillFields.cardholderName !== undefined)
      setCardholderName(prefillFields.cardholderName);
    if (prefillFields.cardNumber !== undefined)
      setCardNumber(formatPan(prefillFields.cardNumber));
    if (prefillFields.expiryDate !== undefined) {
      // Vault stores MM/YYYY; the form uses MM/YY.
      const raw = prefillFields.expiryDate;
      const match = raw.match(/^(\d{2})\/(\d{2}|\d{4})$/);
      if (match) {
        const yy = match[2].length === 4 ? match[2].slice(2) : match[2];
        setExpiryDate(`${match[1]}/${yy}`);
      } else {
        setExpiryDate(raw);
      }
    }
    if (prefillFields.cvv !== undefined) setCvv(prefillFields.cvv);
    if (prefillFields.billingAddressLine1 !== undefined)
      setAddressLine1(prefillFields.billingAddressLine1);
    if (prefillFields.billingAddressLine2 !== undefined)
      setAddressLine2(prefillFields.billingAddressLine2);
    if (prefillFields.billingCity !== undefined)
      setCity(prefillFields.billingCity);
    if (prefillFields.billingPostcode !== undefined)
      setPostcode(prefillFields.billingPostcode);
    if (prefillFields.billingCountry !== undefined)
      setCountry(prefillFields.billingCountry);
  }, [show, prefillFields, existingCardLabel]);

  // Fetch known addresses when modal opens
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const fetchAddresses = async () => {
      setLoadingAddresses(true);
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetUserAddressesResponse>(
          '/memories/user-addresses',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!cancelled) setKnownAddresses(data.addresses || []);
      } catch {
        // Non-critical — user can still type manually
      } finally {
        if (!cancelled) setLoadingAddresses(false);
      }
    };
    fetchAddresses();
    return () => {
      cancelled = true;
    };
  }, [show, getAccessTokenSilently]);

  const handlePrefillAddress = useCallback(
    (idx: number) => {
      const addr = knownAddresses[idx];
      if (!addr) return;
      setAddressLine1(addr.line1);
      setAddressLine2(addr.line2 || '');
      setCity(addr.city);
      setPostcode(addr.postcode);
      setCountry(addr.country);
    },
    [knownAddresses],
  );

  const allRequiredFilled = useMemo(() => {
    return (
      nickname.trim().length > 0 &&
      cardholderName.trim().length > 0 &&
      cardNumber.replace(/\D/g, '').length >= 13 &&
      expiryDate.trim().length > 0 &&
      cvv.trim().length >= 3 &&
      addressLine1.trim().length > 0 &&
      city.trim().length > 0 &&
      postcode.trim().length > 0 &&
      country.trim().length > 0
    );
  }, [
    nickname,
    cardholderName,
    cardNumber,
    expiryDate,
    cvv,
    addressLine1,
    city,
    postcode,
    country,
  ]);

  const resetForm = useCallback(() => {
    setNickname('');
    setCardholderName('');
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setAddressLine1('');
    setAddressLine2('');
    setCity('');
    setPostcode('');
    setCountry('');
    setShowCvv(false);
    setError(null);
    setFieldErrors({});
  }, []);

  // Clear a single field's error message when the user edits it. Keeps
  // the highlighted-field UX from looking stale after a fix.
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!allRequiredFilled || submitting) return;

    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    // ── Pre-flight validation ──────────────────────────────────────
    // We always end up calling the BE's card-validation gate, but
    // catching obvious problems client-side gives a much better UX:
    // the user sees the offending field highlighted instantly instead
    // of a round-trip + generic banner. Keep the checks in lock-step
    // with `dream-be/lib/cardValidation.ts` so they don't drift.
    const network = detectCardNetwork(cardNumber);
    const digitsOnlyNumber = cardNumber.replace(/\D/g, '');
    const localErrors: Record<string, string> = {};
    if (!luhnValid(digitsOnlyNumber)) {
      localErrors.cardNumber =
        'Card number is not valid. Please check the digits.';
    }
    const parsedExpiry = parseExpiry(expiryDate);
    if (!parsedExpiry) {
      localErrors.expiryDate = 'Use MM/YY format, e.g. 04/29.';
    } else if (!expiryIsFuture(parsedExpiry)) {
      localErrors.expiryDate = 'This card has expired.';
    }
    const cvvDigits = cvv.replace(/\D/g, '');
    const expectedCvvLen = network === 'amex' ? 4 : 3;
    if (cvvDigits.length !== expectedCvvLen) {
      localErrors.cvv = `CVV must be ${expectedCvvLen} digits${
        network === 'amex' ? ' (Amex)' : ''
      }.`;
    }
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(
        'Please fix the highlighted fields before saving. Card details must be complete to be useful.',
      );
      setSubmitting(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const lastFour = getLastFour(cardNumber);

      const response = await api.post(
        '/memories/store-credential',
        {
          label: nickname.trim(),
          credentialType: 'card',
          cardLastFour: lastFour,
          cardNetwork: network,
          // When replacing an expired/declined card, tell the BE so it can
          // mark the old credential inactive once this one is saved.
          replacesCredentialId: existingCredentialId || undefined,
          fields: {
            cardholderName: cardholderName.trim(),
            cardNumber: digitsOnlyNumber,
            expiryDate: expiryDate.trim(),
            cvv: cvvDigits,
            billingAddressLine1: addressLine1.trim(),
            billingAddressLine2: addressLine2.trim(),
            billingCity: city.trim(),
            billingPostcode: postcode.trim(),
            billingCountry: country.trim(),
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success && response.data?.credentialId) {
        onSuccess({
          credentialId: response.data.credentialId,
          label: nickname.trim(),
          cardLastFour: lastFour,
          cardNetwork: network,
        });
        resetForm();
      } else {
        setError('Failed to store card. Please try again.');
      }
    } catch (err: any) {
      // 400 from the BE card-validation gate. The response body shape is
      // `{ error, field, code }` — map `field` (BE's vault-field name)
      // onto the FE state key. Most names line up directly.
      const data = err?.response?.data;
      if (err?.response?.status === 400 && data?.field) {
        const beField = String(data.field);
        const feField = beField === 'cardLastFour' ? 'cardNumber' : beField;
        setFieldErrors({ [feField]: String(data.error || 'Invalid value.') });
        setError(
          String(data.error || 'Card details are incomplete or invalid.'),
        );
      } else {
        console.error('Error storing payment card:', err);
        setError('Failed to store card. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    allRequiredFilled,
    submitting,
    getAccessTokenSilently,
    cardNumber,
    nickname,
    cardholderName,
    expiryDate,
    cvv,
    addressLine1,
    addressLine2,
    city,
    postcode,
    country,
    existingCredentialId,
    onSuccess,
    resetForm,
  ]);

  const formatExpiryInput = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    return digits.substring(0, 2) + '/' + digits.substring(2, 4);
  }, []);

  const networkLabel =
    detectedNetwork === 'visa'
      ? 'Visa'
      : detectedNetwork === 'mastercard'
        ? 'Mastercard'
        : detectedNetwork === 'amex'
          ? 'Amex'
          : cardNumber.replace(/\D/g, '').length >= 2
            ? 'Card'
            : '';

  const acknowledgeButton = onAcknowledge ? (
    <button
      className='no-longer-needed-btn'
      onClick={onAcknowledge}
      disabled={submitting}
      type='button'
    >
      <NoLongerNeededIcon /> Remove task
    </button>
  ) : undefined;

  // When rendered as a response artifact (onAcknowledge present), the
  // "Remove task" button replaces Cancel — same pattern as
  // SecureCredentialModal.
  const showCancelButton = !onAcknowledge;

  return (
    <SecureModal
      show={show}
      onClose={handleClose}
      title={isUpdate ? 'Update payment card' : 'Add payment card'}
      shieldText="Card details are stored securely. They're never kept on your device or made available to the assistant."
      submitLabel={isUpdate ? 'Update card' : 'Save card'}
      submittingLabel={isUpdate ? 'Updating…' : 'Saving…'}
      onSubmit={handleSubmit}
      submitDisabled={!allRequiredFilled || readOnly}
      submitting={submitting}
      cancelLabel={showCancelButton ? 'Cancel' : undefined}
      onCancel={showCancelButton ? handleClose : undefined}
      footerLeft={acknowledgeButton}
      backdropClassName='spending-approval-backdrop'
      className='add-payment-card-modal'
    >
      <div className='apc-form'>
        {isUpdate && (
          <div className='apc-update-banner'>
            You&rsquo;re updating the details Dream has stored for
            {existingCardLabel ? (
              <>
                {' '}
                <strong>{existingCardLabel}</strong>
              </>
            ) : (
              <> your card</>
            )}
            .
          </div>
        )}
        <div className='apc-field'>
          <label className='apc-label'>
            Memorable name for this card <span className='apc-required'>*</span>
          </label>
          <input
            className='apc-input'
            type='text'
            placeholder='e.g. My Visa'
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className='apc-field'>
          <label className='apc-label'>
            Name on card <span className='apc-required'>*</span>
          </label>
          <input
            className='apc-input'
            type='text'
            placeholder='e.g. John Smith'
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className='apc-field'>
          <label className='apc-label'>
            Card number <span className='apc-required'>*</span>
          </label>
          <div className='apc-card-number-wrapper'>
            <input
              className={`apc-input${fieldErrors.cardNumber ? ' apc-input-error' : ''}`}
              type='text'
              placeholder='1234 5678 9012 3456'
              value={cardNumber}
              onChange={(e) => {
                setCardNumber(e.target.value);
                clearFieldError('cardNumber');
              }}
              disabled={submitting}
              inputMode='numeric'
              aria-invalid={fieldErrors.cardNumber ? true : undefined}
            />
            {networkLabel && (
              <span
                className={`apc-network-badge card-network-${detectedNetwork}`}
              >
                {networkLabel}
              </span>
            )}
          </div>
          {fieldErrors.cardNumber && (
            <div className='apc-field-error'>{fieldErrors.cardNumber}</div>
          )}
        </div>

        <div className='apc-row'>
          <div className='apc-field apc-field-half'>
            <label className='apc-label'>
              Expiry date <span className='apc-required'>*</span>
            </label>
            <input
              className={`apc-input${fieldErrors.expiryDate ? ' apc-input-error' : ''}`}
              type='text'
              placeholder='MM/YY'
              value={expiryDate}
              onChange={(e) => {
                setExpiryDate(formatExpiryInput(e.target.value));
                clearFieldError('expiryDate');
              }}
              disabled={submitting}
              maxLength={5}
              inputMode='numeric'
              aria-invalid={fieldErrors.expiryDate ? true : undefined}
            />
            {fieldErrors.expiryDate && (
              <div className='apc-field-error'>{fieldErrors.expiryDate}</div>
            )}
          </div>

          <div className='apc-field apc-field-half'>
            <label className='apc-label'>
              CVV <span className='apc-required'>*</span>
            </label>
            <div className='apc-password-wrapper'>
              <input
                className={`apc-input${fieldErrors.cvv ? ' apc-input-error' : ''}`}
                type={showCvv ? 'text' : 'password'}
                placeholder='123'
                value={cvv}
                onChange={(e) => {
                  setCvv(e.target.value);
                  clearFieldError('cvv');
                }}
                disabled={submitting}
                maxLength={4}
                inputMode='numeric'
                aria-invalid={fieldErrors.cvv ? true : undefined}
              />
              <button
                className='apc-toggle-visibility'
                type='button'
                onClick={() => setShowCvv(!showCvv)}
                tabIndex={-1}
              >
                {showCvv ? 'Hide' : 'Show'}
              </button>
            </div>
            {fieldErrors.cvv && (
              <div className='apc-field-error'>{fieldErrors.cvv}</div>
            )}
          </div>
        </div>

        <div className='apc-divider' />

        {knownAddresses.length > 0 && (
          <div className='apc-field'>
            <label className='apc-label'>Pre-fill from saved address</label>
            <select
              className='apc-input apc-address-select'
              defaultValue=''
              onChange={(e) => {
                const idx = parseInt(e.target.value, 10);
                if (!isNaN(idx)) handlePrefillAddress(idx);
              }}
              disabled={submitting}
            >
              <option value='' disabled>
                {loadingAddresses ? 'Loading…' : 'Select an address…'}
              </option>
              {knownAddresses.map((addr, i) => (
                <option key={i} value={i}>
                  {addr.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className='apc-field'>
          <label className='apc-label'>
            Billing address line 1 <span className='apc-required'>*</span>
          </label>
          <input
            className='apc-input'
            type='text'
            placeholder='123 Main Street'
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className='apc-field'>
          <label className='apc-label'>Address line 2</label>
          <input
            className='apc-input'
            type='text'
            placeholder='Apt, suite, etc. (optional)'
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className='apc-row'>
          <div className='apc-field apc-field-half'>
            <label className='apc-label'>
              City <span className='apc-required'>*</span>
            </label>
            <input
              className='apc-input'
              type='text'
              placeholder='London'
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className='apc-field apc-field-half'>
            <label className='apc-label'>
              Postcode / ZIP <span className='apc-required'>*</span>
            </label>
            <input
              className='apc-input'
              type='text'
              placeholder='SW1A 1AA'
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <div className='apc-field'>
          <label className='apc-label'>
            Country <span className='apc-required'>*</span>
          </label>
          <input
            className='apc-input'
            type='text'
            placeholder='United Kingdom'
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={submitting}
          />
        </div>

        {error && <div className='apc-error'>{error}</div>}
      </div>
    </SecureModal>
  );
};

export default AddPaymentCardModal;
